import React from 'react';

import type { PickMediaFn } from './../_shared/media/pick-media';
import type { FetchSitePages } from './../_shared/site-pages/types';
import type { AiRequestService } from './../plugin-ai/utils/use-ai-request-service';
import type { GifsResult } from './../plugin-giphy/types';

/** Context to inject dev mocks */
export const DevContext = React.createContext<DevContextProps>({});

export type DevContextProps = {
  trackRender?: (componentName: string) => void;
  aiRequestService?: AiRequestService;
  giphyRequestService?: {
    getTrendingGifs: (params: {
      path: 'search' | 'trending';
      category: 'gifs' | 'stickers';
      limit: number;
      offset: number;
      searchTag?: string;
    }) => Promise<{ type: string; results: GifsResult }>;
    getSearchGifs: (params: {
      path: 'search' | 'trending';
      category: 'gifs' | 'stickers';
      limit: number;
      offset: number;
      searchTag?: string;
    }) => Promise<{ type: string; results: GifsResult }>;
  };
  pmNodeAttrs?: Record<string, any>;
  fetchSitePages?: FetchSitePages;
  renderCounts?: Map<string, number>;
  pickMedia?: PickMediaFn;
  resetRenderCounts?: () => void;
  onRenderCountChange?: (listener: (renderCounts: Map<string, number>) => void) => () => void;
  connectionState?: ConnectionState;
  setConnectionState?: React.Dispatch<React.SetStateAction<ConnectionState>>;
  logBi?: (params: { dataHook: string; value?: string | boolean | number }) => void;
};

export type DevService = () => DevContextProps;

export const useDevService = () => {
  const service = React.useContext(DevContext);
  const serviceRef = React.useRef<DevContextProps>(service);
  serviceRef.current = service;
  return React.useCallback(() => serviceRef.current, []);
};

export type ConnectionState =
  | { type: 'BROKEN'; delay: number; status: number; response?: any }
  | { type: 'DELAYED'; delay: number }
  | { type: 'NO_MODIFICATION' };

export const useTrackRender = (componentName: string) =>
  React.useContext(DevContext)?.trackRender?.(componentName);

export const useDevGiphyRequestService = () => React.useContext(DevContext)?.giphyRequestService;
export const useDevAiRequestService = () => React.useContext(DevContext)?.aiRequestService;
export const useDevConnectionState = () =>
  (React.useContext(DevContext) as DevContextProps)?.connectionState ?? {
    type: 'NO_MODIFICATION',
  };

export const useDevLogBi = () => {
  return (React.useContext(DevContext) as DevContextProps)?.logBi ?? undefined;
};

export const useDevPmNodeAttrs = () => React.useContext(DevContext)?.pmNodeAttrs ?? undefined;

export const useDevPickMedia = () => React.useContext(DevContext)?.pickMedia ?? undefined;
